import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Download, Users, Building2, Lock, Loader2, Copy, Check, ShieldCheck } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import { passwordComplexityErrors } from '@/utils/passwordComplexity';

const TOKEN_KEY = 'admin_token';

const AdminPage = () => {
  // Auth state
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Session state
  const [adminEmail, setAdminEmail] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Data state
  const [influencerCount, setInfluencerCount] = useState(0);
  const [brandCount, setBrandCount] = useState(0);
  const [admins, setAdmins] = useState([]); // [{email, totp_enrolled, ...}]
  const [peerBusy, setPeerBusy] = useState(false);

  // Change-password state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  // 2FA / forgot-password state machine
  const [phase, setPhase] = useState('login'); // 'login' | '2fa-setup' | '2fa-verify' | 'forgot-password'
  const [tempToken, setTempToken] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupQR, setSetupQR] = useState('');
  const [code, setCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [phaseError, setPhaseError] = useState('');
  const [phaseLoading, setPhaseLoading] = useState(false);

  // Forgot-password state (separate from login email for clarity)
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const t = {
    loginTitle: 'Admin Login',
    emailLabel: 'Email',
    password: 'Password',
    login: 'Login',
    loggingIn: 'Logging in...',
    title: 'Admin Panel',
    subtitle: 'Download form data in Excel format',
    influencers: 'Influencer Applications',
    brands: 'Brand Contact Forms',
    download: 'Download Excel',
    total: 'Total',
    entries: 'entries',
    logout: 'Logout',
    forcedChangeTitle: 'Change Your Password',
    forcedChangeIntro:
      'You are signed in with the bootstrap default password. To continue, please choose a new password.',
    currentPwdLabel: 'Current password',
    newPwdLabel: 'New password',
    confirmPwdLabel: 'Confirm new password',
    submitChange: 'Change password',
    submittingChange: 'Updating...',
    setup2faTitle: 'Set Up Two-Factor Authentication',
    setup2faIntro:
      'Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, Microsoft Authenticator), then enter the 6-digit code below.',
    manualEntry: 'Or enter the secret manually:',
    setup2faVerify: 'Verify & Enable 2FA',
    verify2faTitle: 'Two-Factor Authentication',
    verify2faIntro: 'Enter the 6-digit code from your authenticator app.',
    verify2faSubmit: 'Verify',
    backToLogin: 'Back to login',
    codeLabel: '6-digit code',
  };

  const authedAxios = (overrides = {}) =>
    axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token}`, ...(overrides.headers || {}) },
    });

  const resetAuthChallengeState = () => {
    setPhase('login');
    setTempToken('');
    setSetupSecret('');
    setSetupQR('');
    setCode('');
    setSecretCopied(false);
    setPhaseError('');
    setForgotEmail('');
    setForgotSent(false);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAdminEmail('');
    setMustChangePassword(false);
    setEmail('');
    setPassword('');
    resetAuthChallengeState();
  };

  // Called after a successful TOTP verify (setup or login) — installs the
  // long-lived session token and clears all challenge state.
  const finalizeSession = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setAdminEmail(data.email);
    setMustChangePassword(!!data.must_change_password);
    setPassword('');
    resetAuthChallengeState();
  };

  // On mount or token change: validate session via /admin-me
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        setSessionChecked(true);
        return;
      }
      try {
        const res = await authedAxios().get('/admin-me');
        if (cancelled) return;
        setAdminEmail(res.data.email);
        setMustChangePassword(!!res.data.must_change_password);
        setSessionChecked(true);
      } catch (err) {
        if (cancelled) return;
        // Token invalid/expired — clear and bounce to login
        handleLogout();
        setSessionChecked(true);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [token]);

  // Once authenticated, fetch counts + admin list
  useEffect(() => {
    if (!token || !adminEmail) return;
    const fetchData = async () => {
      try {
        const [inf, br, adminsRes] = await Promise.all([
          authedAxios().get('/influencer-signups'),
          authedAxios().get('/brand-contacts'),
          authedAxios().get('/admin-list'),
        ]);
        setInfluencerCount(inf.data.length);
        setBrandCount(br.data.length);
        setAdmins(adminsRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response && err.response.status === 401) {
          handleLogout();
        }
      }
    };
    fetchData();
  }, [token, adminEmail]);

  const handlePeerReset = async (targetEmail) => {
    const ok = window.confirm(
      `Reset 2FA for ${targetEmail}?\n\n` +
      `Their authenticator-app enrollment will be cleared and they will be ` +
      `prompted to set up 2FA again on their next login. Their password is ` +
      `unchanged.\n\nProceed?`
    );
    if (!ok) return;

    setPeerBusy(true);
    try {
      await authedAxios().post('/admin-peer-2fa-reset', { target_email: targetEmail });
      // Refresh admin list to reflect cleared totp_enrolled flag
      const adminsRes = await authedAxios().get('/admin-list');
      setAdmins(adminsRes.data || []);
      alert(`2FA reset for ${targetEmail}. They will re-enroll on next login.`);
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 400) {
        alert(err.response.data?.detail || 'Peer reset rejected.');
      } else if (status === 401) {
        handleLogout();
      } else {
        alert('Peer reset failed. Please try again.');
      }
    } finally {
      setPeerBusy(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin-login`, { email, password });
      const data = res.data;

      if (data.requires_2fa_setup) {
        // First-time enrollment: ask backend for the QR / secret, then show setup screen
        setTempToken(data.temp_token);
        try {
          const setupRes = await axios.post(`${API}/admin-totp-setup`, {
            temp_token: data.temp_token,
          });
          setSetupSecret(setupRes.data.secret);
          setSetupQR(setupRes.data.qr_data_uri);
          setPhase('2fa-setup');
        } catch (setupErr) {
          setError('Failed to start 2FA setup. Please try logging in again.');
          resetAuthChallengeState();
        }
      } else if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setPhase('2fa-verify');
      } else if (data.token) {
        // Defensive: legacy shape (shouldn't happen with current backend)
        finalizeSession(data);
      } else {
        setError('Unexpected login response. Please try again.');
      }
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 429) {
        setError('Too many attempts. Please wait 15 minutes and try again.');
      } else if (status === 401) {
        setError('Invalid credentials.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetupVerify = async (e) => {
    e.preventDefault();
    setPhaseError('');
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setPhaseError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setPhaseLoading(true);
    try {
      const res = await axios.post(`${API}/admin-totp-verify-setup`, {
        temp_token: tempToken,
        secret: setupSecret,
        code,
      });
      finalizeSession(res.data);
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 401) {
        setPhaseError('Setup session expired. Please log in again.');
        setTimeout(() => resetAuthChallengeState(), 1500);
      } else if (status === 400) {
        setPhaseError(err.response.data?.detail || 'Invalid code. Try again.');
        setCode('');
      } else if (status === 409) {
        setPhaseError('2FA is already enrolled. Please log in again.');
        setTimeout(() => resetAuthChallengeState(), 1500);
      } else {
        setPhaseError('Verification failed. Please try again.');
      }
    } finally {
      setPhaseLoading(false);
    }
  };

  const handleVerifyLogin = async (e) => {
    e.preventDefault();
    setPhaseError('');
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setPhaseError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setPhaseLoading(true);
    try {
      const res = await axios.post(`${API}/admin-totp-verify-login`, {
        temp_token: tempToken,
        code,
      });
      finalizeSession(res.data);
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 401) {
        setPhaseError('Login session expired. Please log in again.');
        setTimeout(() => resetAuthChallengeState(), 1500);
      } else if (status === 400) {
        setPhaseError(err.response.data?.detail || 'Invalid code. Try again.');
        setCode('');
      } else if (status === 429) {
        setPhaseError('Too many attempts. Please wait 15 minutes.');
      } else {
        setPhaseError('Verification failed. Please try again.');
      }
    } finally {
      setPhaseLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setPhaseError('');
    if (!forgotEmail || !forgotEmail.includes('@')) {
      setPhaseError('Please enter a valid email.');
      return;
    }
    setPhaseLoading(true);
    try {
      // Backend always returns a generic success — we honour the same UX
      // (no leak of whether the email exists).
      await axios.post(`${API}/admin-password-reset-request`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 429) {
        setPhaseError('Too many attempts. Please wait 15 minutes and try again.');
      } else {
        // Any other failure: still show the generic confirmation; do not
        // surface backend-side issues that could be probed.
        setForgotSent(true);
      }
    } finally {
      setPhaseLoading(false);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (e) {
      // Clipboard API can fail in non-secure contexts; user can copy manually
      setSecretCopied(false);
    }
  };

  const formatSecret = (s) => (s || '').match(/.{1,4}/g)?.join(' ') || '';

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError('');

    const localErrs = passwordComplexityErrors(newPwd);
    if (localErrs.length > 0) {
      setPwdError('New password missing: ' + localErrs.join(', '));
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError('New password and confirmation do not match.');
      return;
    }
    if (newPwd === currentPwd) {
      setPwdError('New password must differ from current.');
      return;
    }

    setPwdLoading(true);
    try {
      await authedAxios().post('/admin-change-password', {
        current_password: currentPwd,
        new_password: newPwd,
      });
      // Success: clear forced-change flag, drop into normal panel
      setMustChangePassword(false);
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      if (err.response && err.response.status === 400) {
        setPwdError(err.response.data?.detail || 'Password change failed.');
      } else if (err.response && err.response.status === 401) {
        handleLogout();
      } else {
        setPwdError('Password change failed. Please try again.');
      }
    } finally {
      setPwdLoading(false);
    }
  };

  const handleDownload = async (type) => {
    const path = type === 'influencers' ? '/export-influencers' : '/export-brand-contacts';
    try {
      const res = await authedAxios().get(path, { responseType: 'blob' });
      // Trigger browser download
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `${type}_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      if (err.response && err.response.status === 401) {
        handleLogout();
      } else {
        alert('Download failed. Please try again.');
      }
    }
  };

  // Block render until we know whether the stored token is valid
  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="animate-spin text-[#FFDD59]" size={48} />
      </div>
    );
  }

  // 2FA Setup screen (first-time enrollment, after password OK)
  if (!token && phase === '2fa-setup') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative py-12">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-md w-full mx-4">
          <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <ShieldCheck className="mx-auto mb-3" size={48} color="#FFDD59" />
              <h2 className="text-2xl font-bold text-white mb-2">{t.setup2faTitle}</h2>
              <p className="text-sm text-gray-400">{t.setup2faIntro}</p>
            </div>

            {setupQR && (
              <div className="flex justify-center mb-4">
                <img
                  src={setupQR}
                  alt="2FA QR code"
                  className="w-48 h-48 bg-white p-2 rounded-lg"
                />
              </div>
            )}

            <div className="mb-6">
              <p className="text-xs text-gray-400 mb-2">{t.manualEntry}</p>
              <div className="flex gap-2 items-stretch">
                <code className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-[#FFDD59] font-mono text-sm break-all">
                  {formatSecret(setupSecret)}
                </code>
                <button
                  type="button"
                  onClick={handleCopySecret}
                  className="px-3 bg-[#FFDD59] text-black font-semibold rounded-lg hover:bg-yellow-400 flex items-center gap-1 text-sm"
                  aria-label="Copy secret"
                >
                  {secretCopied ? <Check size={16} /> : <Copy size={16} />}
                  {secretCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <form onSubmit={handleSetupVerify} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t.codeLabel}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent text-center text-2xl font-mono tracking-widest"
                />
              </div>

              {phaseError && (
                <div className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm">
                  {phaseError}
                </div>
              )}

              <button
                type="submit"
                disabled={phaseLoading}
                className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {phaseLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Verifying...
                  </>
                ) : t.setup2faVerify}
              </button>

              <button
                type="button"
                onClick={resetAuthChallengeState}
                className="w-full py-2 text-sm text-gray-400 hover:text-white transition"
              >
                {t.backToLogin}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // 2FA Verify screen (returning user, account already enrolled)
  if (!token && phase === '2fa-verify') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-md w-full mx-4">
          <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <ShieldCheck className="mx-auto mb-3" size={48} color="#FFDD59" />
              <h2 className="text-2xl font-bold text-white mb-2">{t.verify2faTitle}</h2>
              <p className="text-sm text-gray-400">{t.verify2faIntro}</p>
            </div>

            <form onSubmit={handleVerifyLogin} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t.codeLabel}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  autoFocus
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent text-center text-2xl font-mono tracking-widest"
                />
              </div>

              {phaseError && (
                <div className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm">
                  {phaseError}
                </div>
              )}

              <button
                type="submit"
                disabled={phaseLoading}
                className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {phaseLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Verifying...
                  </>
                ) : t.verify2faSubmit}
              </button>

              <button
                type="button"
                onClick={resetAuthChallengeState}
                className="w-full py-2 text-sm text-gray-400 hover:text-white transition"
              >
                {t.backToLogin}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password screen — email submit to request reset link
  if (!token && phase === 'forgot-password') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-md w-full mx-4">
          <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <Lock className="mx-auto mb-3" size={48} color="#FFDD59" />
              <h2 className="text-2xl font-bold text-white mb-2">Forgot Your Password?</h2>
              <p className="text-sm text-gray-400">
                {forgotSent
                  ? 'Check your inbox.'
                  : 'Enter your admin email. If it is registered, a reset link will be sent.'}
              </p>
            </div>

            {forgotSent ? (
              <>
                <div className="p-3 bg-green-900/40 border border-green-500/60 text-green-200 rounded-lg text-sm mb-6">
                  If this email is registered, a password reset link has been sent. The link is
                  valid for 1 hour. Your 2FA is unchanged.
                </div>
                <button
                  type="button"
                  onClick={resetAuthChallengeState}
                  className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition"
                >
                  Back to login
                </button>
              </>
            ) : (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-5" noValidate>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Admin email
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    autoComplete="email"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                  />
                </div>

                {phaseError && (
                  <div className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm">
                    {phaseError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={phaseLoading}
                  className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {phaseLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} /> Sending...
                    </>
                  ) : 'Send reset link'}
                </button>

                <button
                  type="button"
                  onClick={resetAuthChallengeState}
                  className="w-full py-2 text-sm text-gray-400 hover:text-white transition"
                >
                  Back to login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!token || !adminEmail) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-md w-full mx-4">
          <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <Lock className="mx-auto mb-4" size={56} color="#FFDD59" />
              <h1 className="text-3xl font-bold text-white mb-2">{t.loginTitle}</h1>
              <p className="text-gray-400">Persona Agency</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6" noValidate>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t.emailLabel}
                </label>
                <input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {t.password}
                </label>
                <PasswordInput
                  id="admin-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> {t.loggingIn}
                  </>
                ) : t.login}
              </button>

              <button
                type="button"
                onClick={() => {
                  setError('');
                  setPhaseError('');
                  setForgotSent(false);
                  setForgotEmail(email);
                  setPhase('forgot-password');
                }}
                className="w-full text-sm text-gray-400 hover:text-[#FFDD59] transition"
              >
                Forgot password?
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Forced password change screen — blocks access to data UI until completed
  if (mustChangePassword) {
    const liveErrs = passwordComplexityErrors(newPwd);
    return (
      <div className="min-h-screen bg-black text-white relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <div className="bg-[#FFDD59] border-b border-yellow-600">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-black">PERSONA ADMIN</h1>
                <p className="text-xs text-black/70">Signed in as {adminEmail}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-black text-[#FFDD59] font-semibold rounded-lg hover:bg-gray-900 transition"
              >
                {t.logout}
              </button>
            </div>
          </div>

          <div className="max-w-md mx-auto px-4 py-12">
            <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <Lock className="mx-auto mb-3" size={48} color="#FFDD59" />
                <h2 className="text-2xl font-bold text-white mb-2">{t.forcedChangeTitle}</h2>
                <p className="text-sm text-gray-400">{t.forcedChangeIntro}</p>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-5" noValidate>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t.currentPwdLabel}
                  </label>
                  <PasswordInput
                    id="current-password"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t.newPwdLabel}
                  </label>
                  <PasswordInput
                    id="new-password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    autoComplete="new-password"
                  />
                  {newPwd.length > 0 && liveErrs.length > 0 && (
                    <ul className="mt-2 text-xs text-yellow-300 list-disc list-inside space-y-1">
                      {liveErrs.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  )}
                  {newPwd.length > 0 && liveErrs.length === 0 && (
                    <p className="mt-2 text-xs text-green-400">✓ Meets complexity requirements</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t.confirmPwdLabel}
                  </label>
                  <PasswordInput
                    id="confirm-password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    autoComplete="new-password"
                  />
                  {confirmPwd.length > 0 && confirmPwd !== newPwd && (
                    <p className="mt-2 text-xs text-red-400">Passwords do not match</p>
                  )}
                </div>

                {pwdError && (
                  <div className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm">
                    {pwdError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={pwdLoading}
                  className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {pwdLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} /> {t.submittingChange}
                    </>
                  ) : t.submitChange}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Panel (after login)
  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="bg-[#FFDD59] border-b border-yellow-600">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-black">PERSONA ADMIN</h1>
              <p className="text-xs text-black/70">Signed in as {adminEmail}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-black text-[#FFDD59] font-semibold rounded-lg hover:bg-gray-900 transition"
            >
              {t.logout}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.title}</h2>
            <p className="text-lg text-gray-400">{t.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Influencer Signups */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-[#FFDD59] rounded-2xl p-8 transform hover:scale-105 transition shadow-2xl">
              <div className="flex items-center gap-4 mb-6">
                <Users size={48} className="text-[#FFDD59]" />
                <div>
                  <h3 className="text-2xl font-bold text-[#FFDD59]">{t.influencers}</h3>
                  <p className="text-gray-400">{t.total}: {influencerCount} {t.entries}</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('influencers')}
                className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition flex items-center justify-center gap-2"
                data-testid="download-influencers"
              >
                <Download size={20} />
                {t.download}
              </button>
            </div>

            {/* Brand Contacts */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-[#FFDD59] rounded-2xl p-8 transform hover:scale-105 transition shadow-2xl">
              <div className="flex items-center gap-4 mb-6">
                <Building2 size={48} className="text-[#FFDD59]" />
                <div>
                  <h3 className="text-2xl font-bold text-[#FFDD59]">{t.brands}</h3>
                  <p className="text-gray-400">{t.total}: {brandCount} {t.entries}</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('brands')}
                className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition flex items-center justify-center gap-2"
                data-testid="download-brands"
              >
                <Download size={20} />
                {t.download}
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-12 bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
            <p className="text-gray-300">
              Excel files will be downloaded automatically. All form data is organized by date.
            </p>
          </div>

          {/* Admin Tools — peer 2FA reset for the other admin */}
          {admins.filter((a) => a.email !== adminEmail).length > 0 && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-white mb-4">Admin Tools</h3>
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 space-y-3">
                <p className="text-sm text-gray-400 mb-4">
                  Reset another admin's 2FA enrollment. They will be prompted to set up
                  their authenticator app again on next login. Their password is not changed.
                </p>
                {admins
                  .filter((a) => a.email !== adminEmail)
                  .map((a) => (
                    <div
                      key={a.email}
                      className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 gap-4"
                    >
                      <div className="min-w-0">
                        <div className="text-white font-semibold truncate">{a.email}</div>
                        <div className="text-xs text-gray-400">
                          2FA: {a.totp_enrolled ? (
                            <span className="text-green-400">Enrolled</span>
                          ) : (
                            <span className="text-yellow-400">Not enrolled</span>
                          )}
                          {a.last_login && ` • Last login: ${a.last_login.slice(0, 19).replace('T', ' ')}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={peerBusy || !a.totp_enrolled}
                        onClick={() => handlePeerReset(a.email)}
                        className="px-4 py-2 bg-red-700 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                        title={a.totp_enrolled ? 'Reset their 2FA' : 'They are not enrolled in 2FA'}
                      >
                        Reset 2FA
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
